import React, { useState } from 'react';
import axios from 'axios';
import config from '../config';
import errorTracker from '../utils/errorTracker';
import logger from '../utils/logger';

export default function Registration() {
  // Registration
  const [first_name, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [shop, setShop] = useState('');
  // Onboarding
  const [domain, setDomain] = useState('');
  const [zone_info, setZoneInfo] = useState(null);

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);





  // Connect store to Cloudflare (combines onboarding and connection)
  const connectStoreToCloudflare = async () => {
    setMessage('');
    setZoneInfo(null);
    
    if (!shop || !domain || !email) { 
      setMessage('Please enter email, shop, and domain'); 
      return; 
    }
    
    try {
      setLoading(true);
      
      // First, validate that the domain exists in Cloudflare
      const zone_resp = await axios.get(`${config.WORKER_BASE_URL}/api/zone-lookup`, {
        params: { domain },
        timeout: config.REQUEST_TIMEOUT_MS,
      });
      
      if (!zone_resp.data?.success) {
        setMessage(`❌ Domain validation failed: ${zone_resp.data?.error || 'Zone not found in Cloudflare'}`);
        return;
      }
      
      // Zone exists, proceed with onboarding (which handles customer creation automatically)
      const resp = await axios.post(`${config.WORKER_BASE_URL}/api/onboard-customer`, { 
        shop, 
        domain,
        email 
      }, { 
        timeout: config.REQUEST_TIMEOUT_MS 
      });
      
      if (resp.data?.success) {
        setZoneInfo({ 
          zone_id: resp.data.zone_id, 
          name: resp.data.domain 
        });
        setMessage(`✅ Successfully connected ${resp.data.shop} to Cloudflare! Zone: ${resp.data.zone_id}`);
        
        // Now initiate Shopify OAuth connection
        try {
          const oauth_resp = await axios.get(`${config.WORKER_BASE_URL}/api/auth/shopify`, { 
            params: { shop }, 
            timeout: config.REQUEST_TIMEOUT_MS 
          });
          if (oauth_resp.data?.auth_url) {
            window.location.href = oauth_resp.data.auth_url;
          } else {
            setMessage('✅ Store connected to Cloudflare! Next: Complete Shopify OAuth connection.');
          }
        } catch (oauthError) {
          setMessage('✅ Store connected to Cloudflare! Shopify OAuth will be available after setup.');
        }
      } else {
        setMessage(`❌ Connection failed: ${resp.data?.error || 'Unknown error'}`);
      }
    } catch (error) {
      logger.error('Connection error:', error);
      errorTracker.trackError(error, {
        page: 'registration',
        action: 'connect_store_to_cloudflare',
        shop: shop,
        domain: domain
      });
      if (error.response?.data?.error) {
        setMessage(`❌ Connection failed: ${error.response.data.error}`);
      } else {
        setMessage('❌ Connection failed: Network or server error');
      }
    } finally { 
      setLoading(false); 
    }
  };



  return (
    <div className="page-content">
      <div className="page-header">
        <h1>Step 1: Quick Start — Store Registration</h1>
        <p className="pricing-subtitle">Create your account, map your domain, and connect to Cloudflare</p>
        
        {/* Real-time Store and Domain Display */}
        <div style={{ 
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '8px',
          fontSize: '14px'
        }}>
          <p className="shop-info" style={{ margin: '0 0 4px 0' }}>
            Store: {shop || 'Not Entered'}
          </p>
          <p className="domain-info" style={{ margin: '0', color: '#0369a1' }}>
            Domain: {domain || 'Not Entered'}
          </p>
        </div>
      </div>

      {/* Quick Start Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        flexWrap: 'wrap', 
        marginBottom: '24px',
        justifyContent: 'center'
      }}>
        <button 
          className="btn btn--primary btn--sm" 
          onClick={() => setMessage('Quick Start 1: Basic Setup - Enter your email, shop, and domain, then click "Connect Store to Cloudflare" to complete setup and connect to Shopify')}
        >
          Quick Start 1
        </button>
        <button 
          className="btn btn--primary btn--sm" 
          onClick={() => setMessage('Quick Start 2: Domain Validation - Your domain is automatically validated when you connect your store to Cloudflare')}
        >
          Quick Start 2
        </button>
        <button 
          className="btn btn--danger btn--sm" 
          onClick={() => setMessage('Quick Start 3: Shopify Integration - Automatically handled when you connect your store to Cloudflare')}
        >
          Quick Start 3
        </button>
      </div>

      <div className="toggle-list" style={{ gap: '16px' }}>
        {/* Registration */}
        <div className="toggle-item">
          <div className="toggle-header">
            <h3 className="toggle-label">First Name</h3>
          </div>
          <input 
            type="text" 
            placeholder="John" 
            value={first_name} 
            onChange={(e)=>setFirstName(e.target.value)} 
            className="quick-start-input"
            style={{ 
              width:'100%', 
              padding:'12px', 
              borderRadius:'8px', 
              border:'2px solid #e5e7eb',
              fontSize: '16px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              transition: 'all 0.2s ease'
            }} 
          />
        </div>

        <div className="toggle-item">
          <div className="toggle-header">
            <h3 className="toggle-label">Company Email</h3>
          </div>
          <input 
            type="email" 
            placeholder="john@company.com" 
            value={email} 
            onChange={(e)=>setEmail(e.target.value)} 
            className="quick-start-input"
            style={{ 
              width:'100%', 
              padding:'12px', 
              borderRadius:'8px', 
              border:'2px solid #e5e7eb',
              fontSize: '16px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              transition: 'all 0.2px ease'
            }} 
          />
        </div>

        <div className="toggle-item">
          <div className="toggle-header">
            <h3 className="toggle-label">Shopify MyShopify.com URL</h3>
          </div>
          <input 
            type="text" 
            placeholder="yourstore.myshopify.com" 
            value={shop} 
            onChange={(e)=>setShop(e.target.value)} 
            className="quick-start-input"
            style={{ 
              width:'100%', 
              padding:'12px', 
              borderRadius:'8px', 
              border:'2px solid #e5e7eb',
              fontSize: '16px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              transition: 'all 0.2s ease'
            }} 
          />
        </div>

        <div className="toggle-item">
          <div className="toggle-header">
            <h3 className="toggle-label">Shopify Domain Name</h3>
          </div>
          <input 
            type="text" 
            placeholder="yourdomain.com" 
            value={domain} 
            onChange={(e)=>setDomain(e.target.value)} 
            className="quick-start-input"
            style={{ 
              width:'100%', 
              padding:'12px', 
              borderRadius:'8px', 
              border:'2px solid #e5e7eb',
              fontSize: '16px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              transition: 'all 0.2s ease'
            }} 
          />
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          marginTop: '24px',
          marginBottom: '24px'
        }}>
          <button 
            className="btn btn--primary"
            onClick={connectStoreToCloudflare} 
            disabled={loading}
          >
            {loading ? 'Connecting...' : 'Connect Store to Cloudflare'}
          </button>
        </div>

        {/* Instant Guidance System */}
        <div className="instant-guidance" style={{ 
          marginTop: '24px',
          padding: '20px',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          borderLeft: '4px solid #f97316'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ 
              fontSize: '20px', 
              marginRight: '12px',
              color: '#f97316'
            }}>💡</span>
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#1e293b'
            }}>Instant Guidance</h3>
          </div>
          
          <div style={{ color: '#475569', lineHeight: '1.6' }}>
            <p style={{ marginBottom: '16px' }}>
              <strong>Step-by-step guide to complete your store registration:</strong>
            </p>
            
            <div style={{ marginBottom: '12px' }}>
              <strong>📧 Company Email:</strong> Enter the email address associated with your business. This will be used for account management and support communications.
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <strong>🏪 Shopify MyShopify.com URL:</strong> Your Shopify store's default URL (e.g., mystore.myshopify.com). This is the URL you see when you first create your Shopify store.
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <strong>🌐 Shopify Domain Name:</strong> Your custom domain that customers will visit (e.g., yourdomain.com). This domain should already be connected to your Shopify store.
            </div>
            
            <div style={{ 
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              fontSize: '14px'
            }}>
              <strong>💡 Pro Tip:</strong> Make sure your custom domain is already connected to your Shopify store before proceeding. If you haven't set up a custom domain yet, you can use your myshopify.com URL temporarily.
            </div>
          </div>
        </div>

                            {zone_info && (
                      <div className="connection-status" style={{ marginTop: '12px' }}>
                        <span className="status-icon">✅</span>
                        <span> Zone: {zone_info.zone_id} ({zone_info.name})</span>
                      </div>
                    )}

        {message && <div className="message">{message}</div>}
      </div>
    </div>
  );
}



